import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import type { ChatModelOption } from './types';

/** Automations run on Codex or the API; Claude Code serves chats only. */
export type AutomationExecutor = 'codex' | 'api';

export interface AutomationModel {
  backend: AutomationExecutor;
  model: string;
  effort: string;
}

export interface AutomationModelRow extends AutomationModel {
  key: string;
  label: string;
  description: string;
  /** `platform` when an operator saved it, `default` for Rudi's built-in choice. */
  source: 'platform' | 'default';
}

export interface AutomationModels {
  schema_version: 1;
  revision: number;
  default: AutomationModel;
  automations: AutomationModelRow[];
  model_options: Record<AutomationExecutor, ChatModelOption[]>;
}

export interface UpdateAutomationModels {
  schema_version: 1;
  expected_revision: number;
  /** A listed automation takes the model; null returns it to the default. Others keep theirs. */
  automations: Record<string, AutomationModel | null>;
}

export const AUTOMATION_EXECUTORS: AutomationExecutor[] = ['codex', 'api'];

export function automationModelsError(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; detail?: string } } })?.response
    ?.data;
  const detail = data?.error ?? data?.detail;
  if (typeof detail === 'string') return detail;
  return error instanceof Error ? error.message : 'Could not save automation models';
}

function isModel(value: AutomationModel | undefined): boolean {
  return (
    !!value &&
    AUTOMATION_EXECUTORS.includes(value.backend) &&
    typeof value.model === 'string' &&
    value.model.length > 0 &&
    typeof value.effort === 'string' &&
    value.effort.length > 0
  );
}

function parseAutomationModels(data: AutomationModels): AutomationModels {
  if (
    data?.schema_version !== 1 ||
    !Number.isSafeInteger(data.revision) ||
    data.revision < 0 ||
    !isModel(data.default) ||
    !Array.isArray(data.automations) ||
    !data.automations.every(
      row =>
        isModel(row) &&
        typeof row.key === 'string' &&
        typeof row.label === 'string' &&
        typeof row.description === 'string' &&
        (row.source === 'platform' || row.source === 'default')
    ) ||
    !AUTOMATION_EXECUTORS.every(backend => {
      const options = data.model_options?.[backend];
      return (
        Array.isArray(options) &&
        options.every(
          option =>
            typeof option?.model === 'string' &&
            typeof option.label === 'string' &&
            Array.isArray(option.efforts) &&
            option.efforts.every(effort => typeof effort === 'string' && effort.length > 0)
        )
      );
    })
  )
    throw new Error('This Rudi connection returned an unsupported automation models format.');
  return data;
}

export function useAutomationModels() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['automation-models'],
    queryFn: async () => {
      try {
        return parseAutomationModels(
          (await api.get<AutomationModels>('/admin/automation-models')).data
        );
      } catch (error) {
        // Rudi releases before the setting answer 404.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 405 || status === 501) return null;
        throw error;
      }
    },
  });
}

export function useUpdateAutomationModels() {
  const api = useApiClient();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateAutomationModels) => {
      const saved = parseAutomationModels(
        (await api.put<AutomationModels>('/admin/automation-models', payload)).data
      );
      const confirmed =
        saved.revision > payload.expected_revision &&
        Object.entries(payload.automations).every(([key, choice]) => {
          const row = saved.automations.find(item => item.key === key);
          if (!row) return false;
          if (choice === null) return row.source === 'default';
          return (
            row.source === 'platform' &&
            row.backend === choice.backend &&
            row.model === choice.model &&
            row.effort === choice.effort
          );
        });
      if (!confirmed)
        throw new Error(
          'Rudi did not confirm these automation models. Reload before trying again.'
        );
      return saved;
    },
    onSuccess: saved => {
      client.setQueryData(['automation-models'], saved);
    },
    onError: async error => {
      if ((error as { response?: { status?: number } })?.response?.status === 409) {
        await client.invalidateQueries({ queryKey: ['automation-models'] });
      }
    },
  });
}
